import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { de } from 'date-fns/locale';
import { format, parse, startOfDay, endOfDay, addDays } from 'date-fns';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { Calendar as CalendarIcon, Plus, Edit, Trash2, Save, X, Clock, FileText } from 'lucide-react';
import 'react-day-picker/dist/style.css';

interface Termin {
  id: string;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  monteur_id: string;
  auftrag_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  monteur?: {
    name: string;
  };
  auftrag?: {
    auftragsnummer: string;
    auftragsbezeichnung: string;
  };
}

interface Monteur {
  id: string;
  name: string;
}

interface Auftrag {
  id: string;
  auftragsnummer: string;
  auftragsbezeichnung: string;
}

function Calendar() {
  const { user, userRole, monteurId } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [termine, setTermine] = useState<Termin[]>([]);
  const [monteure, setMonteure] = useState<Monteur[]>([]);
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: format(new Date().setMinutes(0), "yyyy-MM-dd'T'HH:mm"),
    end_time: format(addDays(new Date().setMinutes(0), 1), "yyyy-MM-dd'T'HH:mm"),
    monteur_id: monteurId || '',
    auftrag_id: '',
  });

  useEffect(() => {
    loadData();
  }, [selectedDate, userRole, monteurId]);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);

      // Load termine for the selected date
      const startTime = startOfDay(selectedDate).toISOString();
      const endTime = endOfDay(selectedDate).toISOString();

      let query = supabase
        .from('termine')
        .select(`
          *,
          monteur:monteure(name),
          auftrag:auftraege(auftragsnummer, auftragsbezeichnung)
        `)
        .gte('start_time', startTime)
        .lte('start_time', endTime)
        .order('start_time');

      // If user is a monteur, only show their own termine
      if (userRole === 'monteur' && monteurId) {
        query = query.eq('monteur_id', monteurId);
      }

      const { data: termineData, error: termineError } = await query;

      if (termineError) throw termineError;

      // Load monteure (for admin view)
      if (userRole === 'admin') {
        const { data: monteureData, error: monteureError } = await supabase
          .from('monteure')
          .select('id, name')
          .order('name');

        if (monteureError) throw monteureError;
        setMonteure(monteureData || []);
      }

      // Load auftraege that are not completed
      let auftraegeQuery = supabase
        .from('auftraege')
        .select('id, auftragsnummer, auftragsbezeichnung')
        .in('status', ['neu', 'begonnen'])
        .order('auftragsdatum');

      // If user is a monteur, only show their assigned orders
      if (userRole === 'monteur' && monteurId) {
        auftraegeQuery = auftraegeQuery.eq('monteur_id', monteurId);
      }

      const { data: auftraegeData, error: auftraegeError } = await auftraegeQuery;

      if (auftraegeError) throw auftraegeError;

      setTermine(termineData || []);
      setAuftraege(auftraegeData || []);
    } catch (error: any) {
      console.error('Error loading calendar data:', error);
      setError('Fehler beim Laden der Kalenderdaten');
    } finally {
      setLoading(false);
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (!user) throw new Error('Nicht angemeldet');
      if (!monteurId && !formData.monteur_id) throw new Error('Kein Monteur ausgewählt');

      const terminData = {
        title: formData.title,
        description: formData.description || null,
        start_time: formData.start_time,
        end_time: formData.end_time,
        monteur_id: userRole === 'admin' ? formData.monteur_id : monteurId,
        auftrag_id: formData.auftrag_id || null,
        created_by: user.id
      };

      if (editingId) {
        const { error } = await supabase
          .from('termine')
          .update(terminData)
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('termine')
          .insert([terminData]);

        if (error) throw error;
      }

      // Reset form and reload data
      setFormData({
        title: '',
        description: '',
        start_time: format(new Date().setMinutes(0), "yyyy-MM-dd'T'HH:mm"),
        end_time: format(addDays(new Date().setMinutes(0), 1), "yyyy-MM-dd'T'HH:mm"),
        monteur_id: monteurId || '',
        auftrag_id: '',
      });
      setShowForm(false);
      setEditingId(null);
      loadData();
    } catch (error: any) {
      console.error('Error saving termin:', error);
      setError('Fehler beim Speichern des Termins: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  const startEditing = (termin: Termin) => {
    setEditingId(termin.id);
    setFormData({
      title: termin.title,
      description: termin.description || '',
      start_time: format(new Date(termin.start_time), "yyyy-MM-dd'T'HH:mm"),
      end_time: format(new Date(termin.end_time), "yyyy-MM-dd'T'HH:mm"),
      monteur_id: termin.monteur_id,
      auftrag_id: termin.auftrag_id || '',
    });
    setShowForm(true);
  };

  const deleteTermin = async (id: string) => {
    if (!confirm('Möchten Sie diesen Termin wirklich löschen?')) {
      return;
    }

    try {
      setError(null);
      const { error } = await supabase
        .from('termine')
        .delete()
        .eq('id', id);

      if (error) throw error;

      loadData();
    } catch (error: any) {
      console.error('Error deleting termin:', error);
      setError('Fehler beim Löschen des Termins');
    }
  };

  const formatTimeRange = (start: string, end: string) => {
    return `${format(new Date(start), 'HH:mm')} - ${format(new Date(end), 'HH:mm')}`;
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CalendarIcon className="text-blue-600" />
            Kalender
          </h1>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setFormData({
                title: '',
                description: '',
                start_time: format(new Date().setMinutes(0), "yyyy-MM-dd'T'HH:mm"),
                end_time: format(addDays(new Date().setMinutes(0), 1), "yyyy-MM-dd'T'HH:mm"),
                monteur_id: monteurId || '',
                auftrag_id: '',
              });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={20} />
            Termin hinzufügen
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <div className="bg-white border rounded-lg p-4">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={de}
                modifiers={{
                  hasEvents: termine.map(t => new Date(t.start_time))
                }}
                modifiersStyles={{
                  hasEvents: {
                    backgroundColor: '#93c5fd',
                    color: '#1e40af'
                  }
                }}
              />
            </div>
          </div>

          {/* Events for selected day */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              Termine am {format(selectedDate, 'dd.MM.yyyy')}
            </h2>
            
            <div className="space-y-4">
              {termine.length > 0 ? (
                termine.map(termin => (
                  <div 
                    key={termin.id} 
                    className="border rounded-lg p-4 hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium">{termin.title}</h3>
                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                          <Clock size={14} />
                          {formatTimeRange(termin.start_time, termin.end_time)}
                        </p>
                        {termin.monteur?.name && (
                          <p className="text-sm text-gray-600 mt-1">
                            Monteur: {termin.monteur.name}
                          </p>
                        )}
                        {termin.auftrag && (
                          <div className="mt-2 flex items-start gap-1 text-sm text-gray-600">
                            <FileText size={14} className="mt-1 flex-shrink-0" />
                            <div>
                              <p>Auftrag: {termin.auftrag.auftragsnummer}</p>
                              <p>{termin.auftrag.auftragsbezeichnung}</p>
                            </div>
                          </div>
                        )}
                        {termin.description && (
                          <p className="mt-2 text-sm text-gray-600">
                            {termin.description}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEditing(termin)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Termin bearbeiten"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => deleteTermin(termin.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Termin löschen"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">
                  Keine Termine an diesem Tag
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Event form modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
              <h2 className="text-xl font-bold mb-4">
                {editingId ? 'Termin bearbeiten' : 'Neuer Termin'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Titel
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                {userRole === 'admin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Monteur
                    </label>
                    <select
                      name="monteur_id"
                      value={formData.monteur_id}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Bitte wählen...</option>
                      {monteure.map(monteur => (
                        <option key={monteur.id} value={monteur.id}>
                          {monteur.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Startzeit
                    </label>
                    <input
                      type="datetime-local"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Endzeit
                    </label>
                    <input
                      type="datetime-local"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Auftrag (optional)
                  </label>
                  <select
                    name="auftrag_id"
                    value={formData.auftrag_id}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Kein Auftrag verknüpft</option>
                    {auftraege.map(auftrag => (
                      <option key={auftrag.id} value={auftrag.id}>
                        {auftrag.auftragsnummer} - {auftrag.auftragsbezeichnung}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Beschreibung (optional)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingId(null);
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center gap-2"
                  >
                    <X size={20} />
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Save size={20} />
                    {editingId ? 'Aktualisieren' : 'Speichern'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Calendar;